import { pnlAxisRowClass, pnlAxisRowHeight } from 'src/components/star-grid/utils/pnlAxisPresentation';
import { DefaultMenuItem, GetContextMenuItemsParams, MenuItemDef, RowClassParams, RowHeightParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { PnlReportGridProps } from 'src/components/star-grid/components/PnlReportGrid';
import { useAgGridExpandedState } from 'src/components/star-grid/hooks/useAgGridExpandedState';
import { useDataSyncText } from 'src/components/star-grid/hooks/useDataSyncText';
import { usePnlGridViewport } from 'src/components/star-grid/hooks/usePnlGridViewport';
import { isStatementModel, statementDimensionColumns, togglePnlColumns } from 'src/components/star-grid/utils/pnlReportColumns';
import { wwStatementRowClass, wwStatementRowHeight, wwStatementSpacingClass } from 'src/components/star-grid/utils/pnlRowPresentation';
import { PnlGridRow } from 'src/components/star-grid/starGridTypes';
import { buildAutoGroupColumnDef, buildGetDataPath, pnlSumAggFunc, pnlGetRowClass } from 'src/components/star-grid/utils/pnlGrid';
import { buildStatementColumns, buildStatementTheme, PNL_ROW_HEIGHTS } from 'src/components/star-grid/utils/pnlStatementGrid';

const useGridMenu = (props: PnlReportGridProps, capture: () => void) => {
  const text = useDataSyncText();
  return useCallback(
    (params: GetContextMenuItemsParams<PnlGridRow>): (DefaultMenuItem | MenuItemDef<PnlGridRow>)[] => {
      const defaults: DefaultMenuItem[] = ['copy', 'copyWithHeaders', 'separator', 'export'];
      const dimensions = props.drillableDimensions.filter((dimension) => !props.groupBy.includes(dimension));
      if (params.node?.footer || !dimensions.length) return defaults;
      return [
        {
          name: text('drillDownBy'),
          subMenu: dimensions.map((dimension) => ({
            name: props.dimensionLabels?.[dimension] ?? dimension,
            action: () => {
              capture();
              props.onDrillDown(dimension);
            }
          }))
        },
        'separator',
        ...defaults
      ];
    },
    [props.drillableDimensions, props.groupBy, props.dimensionLabels, props.onDrillDown, capture, text]
  );
};

const useGridPresentation = (props: PnlReportGridProps, showFilters: boolean) => {
  const { agGridTheme } = useTheme();
  const theme = useMemo(() => buildStatementTheme(agGridTheme), [agGridTheme]);
  const statement = isStatementModel(props.model);
  const columnDefs = useMemo(
    () => [...(statement ? statementDimensionColumns(props.model, props.dimensionLabels) : []), ...buildStatementColumns(props.model, showFilters)],
    [props.model, props.dimensionLabels, statement, showFilters]
  );
  const autoGroupColumnDef = useMemo(
    () => ({
      ...buildAutoGroupColumnDef(props.model.dimensionFields, props.dimensionLabels, props.model.isRollup),
      headerName: props.reportName,
      headerComponent: 'pnlHierarchyHeader',
      headerClass: 'sg-report-hierarchy-header',
      width: 260,
      minWidth: 200,
      cellRendererParams: { suppressCount: true },
      filter: showFilters ? 'agTextColumnFilter' : false,
      floatingFilter: showFilters
    }),
    [props.model.dimensionFields, props.model.isRollup, props.dimensionLabels, props.reportName, showFilters]
  );
  return { theme, columnDefs, autoGroupColumnDef, statement };
};

export const usePnlReportGrid = (props: PnlReportGridProps) => {
  const text = useDataSyncText();
  const [search, setSearch] = useState('');
  const [rowHeight, setRowHeight] = useState<number>(PNL_ROW_HEIGHTS[0]);
  const [showFilters, setShowFilters] = useState(false);
  const { gridRef, captureExpandedState, restoreExpandedState } = useAgGridExpandedState();
  const viewportRef = usePnlGridViewport(props.model.rows.length > 0);
  const presentation = useGridPresentation(props, showFilters);
  const getContextMenuItems = useGridMenu(props, captureExpandedState);
  const getDataPath = useMemo(() => buildGetDataPath(props.model.dimensionFields), [props.model.dimensionFields]);
  const getRowClass = useCallback(
    (params: RowClassParams<PnlGridRow>): string[] =>
      [
        pnlGetRowClass(params) ?? pnlAxisRowClass(params.data) ?? wwStatementRowClass(params.data, props.reportFamily),
        params.data?.axis_kind ? undefined : wwStatementSpacingClass(params.data, props.reportFamily)
      ].filter((className): className is string => Boolean(className)),
    [props.reportFamily]
  );
  const getRowHeight = useCallback(
    (params: RowHeightParams<PnlGridRow>): number =>
      pnlAxisRowHeight(params.data, rowHeight) ?? wwStatementRowHeight(params.data, props.reportFamily, rowHeight),
    [props.reportFamily, rowHeight]
  );
  useEffect(() => {
    gridRef.current?.api?.resetRowHeights();
  }, [rowHeight, props.reportFamily, gridRef]);
  const aggFuncs = useMemo(() => ({ pnlSum: pnlSumAggFunc }), []);
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [props.scaleId, gridRef]);
  const toggleColumns = (): void => togglePnlColumns(gridRef.current?.api);
  return {
    text,
    search,
    setSearch,
    rowHeight,
    setRowHeight,
    showFilters,
    setShowFilters,
    toggleColumns,
    gridRef,
    viewportRef,
    restoreExpandedState,
    getContextMenuItems,
    getDataPath,
    getRowClass,
    getRowHeight,
    aggFuncs,
    ...presentation
  };
};
